import type { FormEvent, KeyboardEvent, ReactNode, RefObject } from 'react';
import { ArrowLeft, SendDiagonal } from 'iconoir-react';
import type { BuddyTourStep } from 'config/buddy/tour';
import BuddyTourCard from './BuddyTourCard';

/** Aria labels and the ask placeholder, already translated. */
export type BuddyLabels = {
  dismiss: string;
  region: string;
  ask: string;
  back: string;
  send: string;
};
export type AskSendEvent = FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>;

type BuddyAskFormProps = {
  askRef: RefObject<HTMLTextAreaElement>;
  askText: string;
  labels: BuddyLabels;
  onAskTextChange: (text: string) => void;
  onSend: (event: AskSendEvent) => void;
  onBack: () => void;
};

function BuddyAskForm({ askRef, askText, labels, onAskTextChange, onSend, onBack }: Readonly<BuddyAskFormProps>) {
  return (
    <form className="klax-buddy__ask" onSubmit={onSend}>
      <textarea
        ref={askRef}
        value={askText}
        placeholder={labels.ask}
        aria-label={labels.ask}
        rows={2}
        onChange={(event) => onAskTextChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) onSend(event);
        }}
      />
      <div className="klax-buddy__ask-actions">
        <button type="button" className="klax-buddy__back" aria-label={labels.back} onClick={onBack}>
          <ArrowLeft width={13} height={13} strokeWidth={2} />
        </button>
        <button type="submit" className="klax-buddy__send" aria-label={labels.send} disabled={!askText.trim()}>
          <SendDiagonal width={13} height={13} strokeWidth={2} />
        </button>
      </div>
    </form>
  );
}

function BuddyWords({ typed }: Readonly<{ typed: string }>) {
  return (
    <span className="klax-buddy__copy">
      <span className="klax-buddy__words">
        {typed || (
          <span className="klax-buddy__dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        )}
      </span>
    </span>
  );
}

type BuddySpeechProps = {
  touring: boolean;
  typing: boolean;
  typed: string;
  asking: boolean;
  canAsk: boolean;
  bubbleSide: 'left' | 'right';
  theme: 'light' | 'dark';
  labels: BuddyLabels;
  tourCopy: BuddyTourStep;
  /** i18next context for the tour copy: `informal` for du, none for Sie. */
  addressContext?: string;
  onTourClose: () => void;
  onTourOkay: () => void;
  askRef: RefObject<HTMLTextAreaElement>;
  askText: string;
  onAskTextChange: (text: string) => void;
  onAskSend: (event: AskSendEvent) => void;
  onAskOpen: () => void;
  onAskClose: () => void;
  dismissButton: ReactNode;
};

/** Buddy's speech bubble: the tour card, the ask form, or the typed reply. */
export default function BuddySpeech({
  touring,
  typing,
  typed,
  asking,
  canAsk,
  bubbleSide,
  theme,
  labels,
  tourCopy,
  addressContext,
  onTourClose,
  onTourOkay,
  askRef,
  askText,
  onAskTextChange,
  onAskSend,
  onAskOpen,
  onAskClose,
  dismissButton,
}: Readonly<BuddySpeechProps>) {
  let content = <BuddyWords typed={typed} />;
  if (touring) {
    content = (
      <BuddyTourCard
        copy={tourCopy}
        context={addressContext}
        onClose={onTourClose}
        onOkay={onTourOkay}
      />
    );
  } else if (asking && canAsk) {
    content = (
      <BuddyAskForm
        askRef={askRef}
        askText={askText}
        labels={labels}
        onAskTextChange={onAskTextChange}
        onSend={onAskSend}
        onBack={onAskClose}
      />
    );
  }
  const showAskToggle = !touring && !asking && !typing && canAsk;

  return (
    <div
      className="klax-buddy__bubble"
      data-od-id="buddy-bubble"
      data-open={touring || typing || typed ? 'true' : 'false'}
      data-place="side"
      data-align="left"
      data-side={bubbleSide}
      data-theme={theme}
      data-asking={asking ? 'true' : 'false'}
      data-tour={touring ? 'true' : 'false'}
      data-loading={!touring && !typed && typing ? 'true' : 'false'}
      aria-live="polite"
    >
      <span className="klax-buddy__tail" aria-hidden="true" />
      {content}
      {showAskToggle ? (
        <button
          type="button"
          className="klax-buddy__ask-toggle"
          aria-label={labels.ask}
          aria-expanded={false}
          onClick={(event) => {
            event.stopPropagation();
            onAskOpen();
          }}
        >
          <SendDiagonal width={13} height={13} strokeWidth={2} />
        </button>
      ) : null}
      {touring ? null : dismissButton}
    </div>
  );
}
